import axios, { type AxiosInstance, type Canceler } from 'axios'
import type { Handler } from 'mitt'

import Emitter, { type Events } from './emitter'
import {
  createRequestError,
  ERR_REQUEST_INTERCEPTOR,
  ERR_RESPONSE_INTERCEPTOR,
  ERR_UNKNOWN,
} from './error'
import { InterceptorManager } from './interceptor'
import type {
  InterceptorType,
  InternalRequestConfig,
  RequestConfig,
  RequestInterceptor,
  RequestOptions,
  RequestSkipperCallback,
  ResponseInterceptor,
  ResponseSkipperCallback,
  TrResponse,
} from './types'
import { getRequestHash, isCanceledError, mergeAbortSignals } from './utils'

export default class Request {
  #service: AxiosInstance

  #emitter?: Emitter

  #initConfig: RequestOptions

  #abortKeys: string[] = []

  #defaultInterceptorType: InterceptorType

  #interceptorManager: InterceptorManager = new InterceptorManager()

  constructor({ defaultInterceptorType = 'base', ...restConfig }: RequestOptions = {}) {
    this.#service = axios.create(restConfig)

    this.#initConfig = restConfig
    this.#defaultInterceptorType = defaultInterceptorType

    this.#useInterceptors()
  }

  get emitter(): Emitter | undefined {
    return this.#emitter
  }

  /** @internal */
  get interceptorManager(): InterceptorManager {
    return this.#interceptorManager
  }

  #removeAbortHash(key: string): void {
    const index = this.#abortKeys.indexOf(key)

    if (index !== -1) {
      this.#abortKeys.splice(index, 1)
    }
  }

  #useInterceptors(): void {
    this.#service.interceptors.request.use(async (config: InternalRequestConfig) => {
      try {
        const newConfig = await this.#interceptorManager.doRequestIntercept(
          config,
          config.interceptorType ?? this.#defaultInterceptorType,
        )

        return newConfig
      } catch (err: any) {
        // 这里抛出的错误会被 axios 捕获，然后进入 response 的错误处理流程
        throw createRequestError(err, { config }, ERR_REQUEST_INTERCEPTOR)
      }
    })

    this.#service.interceptors.response.use(async (res: TrResponse) => {
      const config: InternalRequestConfig = res.config

      try {
        const response = await this.#interceptorManager.doResponseIntercept(
          res,
          config.interceptorType ?? this.#defaultInterceptorType,
        )

        return response
      } catch (err: any) {
        throw createRequestError(
          err,
          { config, request: res.request, response: res },
          ERR_RESPONSE_INTERCEPTOR,
        )
      }
    })

    // 这个拦截器用于统一处理所有错误，并触发相应的事件
    // 这样可以避免在每个请求中都要重复处理错误
    this.#service.interceptors.response.use(
      (res) => res,
      (err: any) => {
        if (isCanceledError(err)) {
          this.#emit('warn', err)

          return Promise.reject(err)
        }

        const wrapped = createRequestError(err, err, ERR_UNKNOWN)

        this.#emit('error', wrapped)

        return Promise.reject(wrapped)
      },
    )
  }

  #setInterceptorManager(manager: InterceptorManager) {
    this.#interceptorManager = manager
  }

  #setEmitter(emitter?: Emitter) {
    this.#emitter = emitter
  }

  #emit(event: keyof Events, payload: Events[typeof event]) {
    if (!this.#emitter) return

    this.#emitter.emit(event, payload)
  }

  /**
   * 添加请求拦截
   *
   * @param interceptor :RequestCallBack
   * @returns :removeFunction
   */
  public addRequestInterceptors(...interceptors: RequestInterceptor[]): void {
    for (const item of interceptors) {
      this.#interceptorManager.addRequestInterceptor(
        typeof item === 'function' ? { callback: item } : item,
      )
    }
  }

  public removeRequestInterceptors(...interceptors: RequestInterceptor[]) {
    for (const item of interceptors) {
      this.#interceptorManager.removeRequestInterceptor(item)
    }
  }

  public removeResponseInterceptors(...interceptor: ResponseInterceptor[]) {
    for (const item of interceptor) {
      this.#interceptorManager.removeResponseInterceptor(item)
    }
  }

  public clearResponseInterceptors(type?: InterceptorType) {
    this.#interceptorManager.clearResponseInterceptors(type)
  }

  public clearRequestInterceptors(type?: InterceptorType) {
    this.#interceptorManager.clearRequestInterceptors(type)
  }

  /**
   * 添加响应拦截
   * @param interceptor :ResponseCallBack
   * @returns :removeFunction
   */
  public addResponseInterceptors<T = unknown>(...interceptors: ResponseInterceptor<T>[]): void {
    for (const item of interceptors) {
      this.#interceptorManager.addResponseInterceptor<T>(
        typeof item === 'function' ? { callback: item } : item,
      )
    }
  }

  /**
   * 添加事件监听
   * @param event :eventName
   * @param handler :handler
   */
  public addEventListener<K extends keyof Events>(event: K, handler: Handler<Events[K]>) {
    if (!this.#emitter) {
      this.#emitter = new Emitter()
    }
    this.#emitter.on(event, handler)
  }

  /**
   * 筛选url，跳过响应拦截器
   * @param skipper : (url:string)=>boolean
   */
  public setResponseInterceptorSkipper(skipper: ResponseSkipperCallback) {
    this.#interceptorManager.setResponseSkipper(skipper)
  }

  /**
   * 筛选url，跳过请求拦截器
   * @param skipper : (url:string)=>boolean
   */
  public setRequestInterceptorSkipper(skipper: RequestSkipperCallback) {
    this.#interceptorManager.setRequestSkipper(skipper)
  }

  /**
   * 克隆当前状态的request实例
   * @param config :like Axios Config
   * @returns TrRequest
   */
  public clone(config?: RequestOptions): Request {
    const service = new Request({
      ...this.#initConfig,
      defaultInterceptorType: this.#defaultInterceptorType,
      ...config,
    })

    service.#setInterceptorManager(this.#interceptorManager.clone())
    service.#setEmitter(this.#emitter)

    return service
  }

  public getUri(config?: RequestConfig): string {
    return this.#service.getUri(config)
  }

  public setBaseURL(baseURL: string): void {
    this.#service.defaults.baseURL = baseURL
  }

  public request<R = TrResponse, D = any>(
    url: string,
    config?: Omit<RequestConfig<D>, 'url'>,
  ): Promise<R>
  public request<R = TrResponse, D = any>(config: RequestConfig<D>): Promise<R>
  public async request<R = TrResponse, D = any>(
    configOrUrl?: RequestConfig<D> | string,
    config?: Omit<RequestConfig<D>, 'url'>,
  ): Promise<R> {
    const { signal: userSignal, ...restConfig }: RequestConfig =
      typeof configOrUrl === 'string' ? { ...config, url: configOrUrl } : { ...configOrUrl }

    const { cancel, signal } = mergeAbortSignals(userSignal)

    const newConfig: RequestConfig<D> = { ...restConfig, signal }

    const key = getRequestHash(newConfig)

    try {
      this.#addAbortKeys(key, newConfig, cancel)

      const data = await this.#service.request(newConfig)

      return data as R
    } finally {
      this.#removeAbortHash(key)
    }
  }

  #addAbortKeys(key: string, config: RequestConfig, cancel: Canceler): void {
    if (this.#abortKeys.includes(key)) {
      this.#emit('duplicate', { config, cancel })
    } else {
      this.#abortKeys.push(key)
    }
  }

  public get<R = TrResponse, P = any>(
    url?: string,
    params?: P,
    config?: Omit<RequestConfig, 'params'>,
  ): Promise<R> {
    return this.request<R>({ url, ...config, method: 'get', params })
  }

  public post<R = TrResponse, D = any>(url?: string, data?: D, config?: RequestConfig): Promise<R> {
    return this.request<R, D>({ url, data, ...config, method: 'post' })
  }

  public put<R = TrResponse, D = any>(
    url?: string,
    data?: D,
    config?: Omit<RequestConfig, 'data'>,
  ): Promise<R> {
    return this.request<R, D>({ url, data, ...config, method: 'put' })
  }

  public delete<R = TrResponse, D = any>(
    url?: string,
    data?: D,
    config?: Omit<RequestConfig, 'data'>,
  ): Promise<R> {
    return this.request<R, D>({ url, data, ...config, method: 'delete' })
  }

  public patch<R = TrResponse, D = any>(
    url?: string,
    data?: D,
    config?: Omit<RequestConfig, 'data'>,
  ): Promise<R> {
    return this.request<R, D>({ url, data, ...config, method: 'patch' })
  }

  public head<R = TrResponse>(url?: string, config?: RequestConfig<void>): Promise<R> {
    return this.request<R>({ url, ...config, method: 'head' })
  }

  public options<R = TrResponse>(url?: string, config?: RequestConfig<void>): Promise<R> {
    return this.request<R>({ url, ...config, method: 'options' })
  }
}
